import { useEffect, useRef, useState, type ElementType } from 'react';
import { Badge, Box, Flex, Icon, Spinner, Text } from '@chakra-ui/react';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import type { InvoiceTypeKey, TopFlaggedVendorTypeRow } from '../../../types/reporting';

const TYPE_LABEL: Record<InvoiceTypeKey, string> = {
    received: 'Received',
    returned: 'Credited',
    return_slip: 'Return Slip',
    sublet: 'Sublet',
};

const TYPE_COLOR: Record<InvoiceTypeKey, string> = {
    received: 'blue',
    returned: 'red',
    return_slip: 'yellow',
    sublet: 'green',
};

const percent = (value: number) => `${value.toFixed(1)}%`;
const PAGE_SIZE = 5;

export function TopFlaggedVendorTypes({
    rows,
    hasMore = false,
    resetKey,
    onLoadMore,
    onRowClick,
}: {
    rows: TopFlaggedVendorTypeRow[] | undefined;
    hasMore?: boolean;
    /** Changes when the reporting filters change, which drops any later pages. */
    resetKey: string;
    onLoadMore?: (offset: number) => Promise<{ rows: TopFlaggedVendorTypeRow[]; hasMore: boolean }>;
    onRowClick?: (row: TopFlaggedVendorTypeRow) => void;
}) {
    const [extraRows, setExtraRows] = useState<TopFlaggedVendorTypeRow[]>([]);
    const [shownExtra, setShownExtra] = useState(0);
    const [more, setMore] = useState(hasMore);
    const [loadingMore, setLoadingMore] = useState(false);
    const requestGeneration = useRef(0);

    useEffect(() => {
        requestGeneration.current += 1;
        setExtraRows([]);
        setShownExtra(0);
        setMore(hasMore);
        setLoadingMore(false);
    }, [resetKey, hasMore]);

    if (!rows || rows.length === 0) {
        return (
            <Text color="gray.500" fontSize="sm">
                No vendor and invoice type with at least 10 invoices in this period.
            </Text>
        );
    }

    const visibleRows = [...rows, ...extraRows.slice(0, shownExtra)];
    const showUp = shownExtra > 0;
    const showDown = shownExtra < extraRows.length || more;

    const showPrevious = () => {
        setShownExtra((count) => Math.max(0, count - PAGE_SIZE));
    };

    const showNext = async () => {
        if (shownExtra < extraRows.length) {
            setShownExtra((count) => Math.min(extraRows.length, count + PAGE_SIZE));
            return;
        }
        if (!onLoadMore || loadingMore) return;
        const generation = requestGeneration.current;
        setLoadingMore(true);
        try {
            const page = await onLoadMore(rows.length + extraRows.length);
            if (generation !== requestGeneration.current) return;
            const seen = new Set(
                [...rows, ...extraRows].map((row) => `${row.vendorName}\0${row.invoiceType}`),
            );
            const fresh = page.rows.filter((row) => !seen.has(`${row.vendorName}\0${row.invoiceType}`));
            if (fresh.length === 0) {
                setMore(false);
                return;
            }
            setExtraRows((current) => [...current, ...fresh]);
            setShownExtra((count) => count + fresh.length);
            setMore(page.hasMore);
        } finally {
            if (generation === requestGeneration.current) setLoadingMore(false);
        }
    };

    return (
        <Flex direction="column" h="100%" minH={0}>
            {showUp && (
                <ChevronButton
                    direction="up"
                    label="Show the previous 5 ranked slices"
                    onClick={showPrevious}
                />
            )}
            <Box flex="1" minH={0} overflowY="auto">
            <Flex
                px={2}
                py={1}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.04em"
            >
                <Text w="28px" flexShrink={0} />
                <Text flex="1.6" minW={0}>Vendor</Text>
                <Text flex="1">Type</Text>
                <Text flex="0.7" textAlign="right">Invoices</Text>
                <Text flex="0.8" textAlign="right">Auto-submit</Text>
            </Flex>
            {visibleRows.map((row, index) => {
                const clickable = Boolean(onRowClick);
                return (
                    <Flex
                        key={`${row.vendorName}-${row.invoiceType}`}
                        as="button"
                        type="button"
                        w="100%"
                        px={2}
                        py={2}
                        align="center"
                        textAlign="left"
                        borderTop="1px solid"
                        borderColor="gray.100"
                        cursor={clickable ? 'pointer' : 'default'}
                        _hover={clickable ? { bg: 'gray.50' } : undefined}
                        onClick={clickable ? () => onRowClick?.(row) : undefined}
                    >
                        <Text w="28px" flexShrink={0} fontSize="sm" fontWeight="semibold" color="gray.500">
                            {index + 1}
                        </Text>
                        <Text flex="1.6" minW={0} fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
                            {row.vendorName}
                        </Text>
                        <Box flex="1">
                            <Badge colorScheme={TYPE_COLOR[row.invoiceType]} variant="subtle" fontSize="10px">
                                {TYPE_LABEL[row.invoiceType]}
                            </Badge>
                        </Box>
                        <Text flex="0.7" textAlign="right" fontSize="sm" color="gray.700">
                            {row.invoiceCount.toLocaleString()}
                        </Text>
                        <Text flex="0.8" textAlign="right" fontSize="sm" color="gray.700">
                            {percent(row.autoSubmissionRate)}
                        </Text>
                    </Flex>
                );
            })}
            </Box>
            {showDown && (
                <ChevronButton
                    direction="down"
                    label="Show the next 5 ranked slices"
                    loading={loadingMore}
                    onClick={() => { void showNext(); }}
                />
            )}
        </Flex>
    );
}

function ChevronButton({
    direction,
    label,
    loading = false,
    onClick,
}: {
    direction: 'up' | 'down';
    label: string;
    loading?: boolean;
    onClick: () => void;
}) {
    return (
        <Flex
            as="button"
            type="button"
            w="100%"
            justify="center"
            align="center"
            py={1}
            flexShrink={0}
            color="gray.500"
            aria-label={label}
            disabled={loading}
            onClick={onClick}
            _hover={{ bg: 'gray.50' }}
        >
            {loading
                ? <Spinner size="xs" />
                : <Icon as={(direction === 'down' ? TbChevronDown : TbChevronUp) as ElementType} boxSize={4} />}
        </Flex>
    );
}
